import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { FixedAssetsPageView } from './FixedAssetsPageView';
import { useFixedAssetsPage } from './useFixedAssetsPage';

/**
 * `/accounting/fixed-assets` — the Fixed Assets register.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner Content
 * child, since React does not catch a component's own throws (the repo's
 * self-served page pattern).
 */
export const FixedAssetsPage: React.FC = () => {
    const page = useFixedAssetsPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('fixedAssets.error.title'),
            errorMessage: t('fixedAssets.error.message'),
            errorRetry: t('fixedAssets.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('fixed-assets-page')}
        >
            <ErrorBoundary
                data-testid="fixed-assets-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="fixed-assets-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <FixedAssetsPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type FixedAssetsPageContentProps = {
    page: ReturnType<typeof useFixedAssetsPage>;
};

const FixedAssetsPageContent: React.FC<FixedAssetsPageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    // The View keeps its summary labels, toolbar and CTAs while loading and
    // skeletons only the values and rows — the repo's loading-state standard.
    return (
        <FixedAssetsPageView
            figures={page.figures}
            isLoading={page.isLoading || page.figures === null}
            page={page}
        />
    );
};
